/**
 * BLS12-381 CryptoSuite implementation
 *
 * Concrete implementation using @noble/curves BLS12-381 for curve operations
 * and @mattrglobal/pairing-crypto for BBS+ signatures.
 *
 * All direct crypto library imports are confined to this file (R16.2).
 *
 * @traces R16.2, Architecture §7.5
 */
import { bls12_381 } from '@noble/curves/bls12-381';
import { sha256 } from '@noble/hashes/sha256';
import { hmac } from '@noble/hashes/hmac';
import { ed25519 } from '@noble/curves/ed25519';
import { secp256k1 } from '@noble/curves/secp256k1';
import { bbs } from '@mattrglobal/pairing-crypto';
const G1 = bls12_381.G1;
const ProjectivePoint = G1.ProjectivePoint;
const Fr = ProjectivePoint.Fn;
/** Hash-to-curve DST for OwnYou pseudonym/tracking_ID generation */
const OWNYOU_DST = 'OwnYou-BBS-Plus-v1';
/** DST for single-base Schnorr challenge hashing */
const SCHNORR_DST = new TextEncoder().encode('OwnYou-Schnorr-v1');
/** DST for two-base DLEQ challenge hashing */
const DLEQ2_DST = new TextEncoder().encode('OwnYou-DLEQ2-v1');
/**
 * Convert Uint8Array to bigint scalar, reduced mod Fr.ORDER
 */
function bytesToScalar(bytes) {
    let n = 0n;
    for (const b of bytes) {
        n = (n << 8n) | BigInt(b);
    }
    return Fr.create(n);
}
/**
 * Convert bigint scalar to 32-byte Uint8Array (big-endian)
 */
function scalarToBytes(scalar) {
    const bytes = new Uint8Array(32);
    let n = scalar;
    for (let i = 31; i >= 0; i--) {
        bytes[i] = Number(n & 0xffn);
        n >>= 8n;
    }
    return bytes;
}
/**
 * Deserialize a compressed G1 point from bytes
 */
function pointFromBytes(bytes) {
    return ProjectivePoint.fromHex(bytes);
}
/**
 * Serialize a G1 point to compressed 48-byte form
 */
function pointToBytes(point) {
    return point.toRawBytes(true);
}
/**
 * Length-prefixed SHA-256 over a list of byte chunks.
 * Each chunk is preceded by its big-endian uint32 length, preventing the
 * concatenation ambiguity that would allow encoding collisions.
 */
function lengthPrefixedHash(chunks) {
    const total = chunks.reduce((n, c) => n + 4 + c.length, 0);
    const buf = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) {
        new DataView(buf.buffer, buf.byteOffset + off, 4).setUint32(0, c.length, false);
        off += 4;
        buf.set(c, off);
        off += c.length;
    }
    return sha256(buf);
}
function assertNonIdentity(p, name) {
    if (p.equals(ProjectivePoint.ZERO)) {
        throw new Error(`${name}: identity point rejected`);
    }
}
function assertNonZeroScalar(s, name) {
    if (Fr.create(s) === 0n) {
        throw new Error(`${name}: zero scalar rejected`);
    }
}
function assertUint8Array(v, name, expectedLen) {
    if (!(v instanceof Uint8Array)) {
        throw new TypeError(`${name}: expected Uint8Array`);
    }
    if (expectedLen !== undefined && v.length !== expectedLen) {
        throw new TypeError(`${name}: expected ${expectedLen} bytes, got ${v.length}`);
    }
}
export function createBLS12381Suite() {
    return {
        hashToCurve(scope) {
            assertUint8Array(scope, 'hashToCurve.scope');
            const point = G1.hashToCurve(scope, { DST: OWNYOU_DST });
            return pointToBytes(point);
        },
        scalarMultiply(point, scalar) {
            assertUint8Array(point, 'scalarMultiply.point', 48);
            assertUint8Array(scalar, 'scalarMultiply.scalar');
            const p = pointFromBytes(point);
            assertNonIdentity(p, 'scalarMultiply.point');
            const s = bytesToScalar(scalar);
            assertNonZeroScalar(s, 'scalarMultiply.scalar');
            return pointToBytes(p.multiply(s));
        },
        elgamalEncrypt(point, publicKey) {
            assertUint8Array(point, 'elgamalEncrypt.point', 48);
            assertUint8Array(publicKey, 'elgamalEncrypt.publicKey', 48);
            const M = pointFromBytes(point);
            assertNonIdentity(M, 'elgamalEncrypt.point');
            const pk = pointFromBytes(publicKey);
            assertNonIdentity(pk, 'elgamalEncrypt.publicKey');
            const r = bytesToScalar(this.randomScalar());
            const c1 = ProjectivePoint.BASE.multiply(r);
            const c2 = M.add(pk.multiply(r));
            return { c1: pointToBytes(c1), c2: pointToBytes(c2) };
        },
        elgamalReblind(ciphertext, publicKey) {
            assertUint8Array(publicKey, 'elgamalReblind.publicKey', 48);
            const c1 = pointFromBytes(ciphertext.c1);
            const c2 = pointFromBytes(ciphertext.c2);
            const pk = pointFromBytes(publicKey);
            assertNonIdentity(pk, 'elgamalReblind.publicKey');
            const r2 = bytesToScalar(this.randomScalar());
            const newC1 = c1.add(ProjectivePoint.BASE.multiply(r2));
            const newC2 = c2.add(pk.multiply(r2));
            return { c1: pointToBytes(newC1), c2: pointToBytes(newC2) };
        },
        elgamalDecrypt(ciphertext, secretKey) {
            assertUint8Array(secretKey, 'elgamalDecrypt.secretKey');
            const c1 = pointFromBytes(ciphertext.c1);
            const c2 = pointFromBytes(ciphertext.c2);
            const sk = bytesToScalar(secretKey);
            const decrypted = c2.add(c1.multiply(sk).negate());
            return pointToBytes(decrypted);
        },
        generateDLEQProof(point, scalar, basePoint, context) {
            assertUint8Array(point, 'generateDLEQProof.point', 48);
            assertUint8Array(scalar, 'generateDLEQProof.scalar');
            assertUint8Array(basePoint, 'generateDLEQProof.basePoint', 48);
            assertUint8Array(context, 'generateDLEQProof.context');
            const P = pointFromBytes(point);
            const B = pointFromBytes(basePoint);
            assertNonIdentity(P, 'generateDLEQProof.point');
            assertNonIdentity(B, 'generateDLEQProof.basePoint');
            const s = bytesToScalar(scalar);
            assertNonZeroScalar(s, 'generateDLEQProof.scalar');
            const k = bytesToScalar(this.randomScalar());
            const R1 = B.multiply(k);
            const challenge = lengthPrefixedHash([
                SCHNORR_DST,
                pointToBytes(B),
                pointToBytes(P),
                pointToBytes(R1),
                context,
            ]);
            const e = bytesToScalar(challenge);
            const z = Fr.create(Fr.sub(k, Fr.mul(e, s)));
            return {
                R: pointToBytes(R1),
                z: scalarToBytes(z),
            };
        },
        verifyDLEQProof(proof, point, basePoint, context) {
            assertUint8Array(point, 'verifyDLEQProof.point', 48);
            assertUint8Array(basePoint, 'verifyDLEQProof.basePoint', 48);
            assertUint8Array(context, 'verifyDLEQProof.context');
            assertUint8Array(proof.R, 'verifyDLEQProof.proof.R', 48);
            assertUint8Array(proof.z, 'verifyDLEQProof.proof.z', 32);
            let P;
            let B;
            let R;
            try {
                P = pointFromBytes(point);
                B = pointFromBytes(basePoint);
                R = pointFromBytes(proof.R);
            }
            catch {
                return false;
            }
            if (R.equals(ProjectivePoint.ZERO))
                return false;
            const z = bytesToScalar(proof.z);
            const challenge = lengthPrefixedHash([
                SCHNORR_DST,
                pointToBytes(B),
                pointToBytes(P),
                pointToBytes(R),
                context,
            ]);
            const e = bytesToScalar(challenge);
            const lhs = B.multiply(z).add(P.multiply(e));
            return lhs.equals(R);
        },
        generateDLEQ2Proof(point1, point2, scalar, basePoint1, basePoint2, context) {
            assertUint8Array(point1, 'generateDLEQ2Proof.point1', 48);
            assertUint8Array(point2, 'generateDLEQ2Proof.point2', 48);
            assertUint8Array(scalar, 'generateDLEQ2Proof.scalar');
            assertUint8Array(basePoint1, 'generateDLEQ2Proof.basePoint1', 48);
            assertUint8Array(basePoint2, 'generateDLEQ2Proof.basePoint2', 48);
            assertUint8Array(context, 'generateDLEQ2Proof.context');
            const P1 = pointFromBytes(point1);
            const P2 = pointFromBytes(point2);
            const B1 = pointFromBytes(basePoint1);
            const B2 = pointFromBytes(basePoint2);
            assertNonIdentity(P1, 'generateDLEQ2Proof.point1');
            assertNonIdentity(P2, 'generateDLEQ2Proof.point2');
            assertNonIdentity(B1, 'generateDLEQ2Proof.basePoint1');
            assertNonIdentity(B2, 'generateDLEQ2Proof.basePoint2');
            const s = bytesToScalar(scalar);
            assertNonZeroScalar(s, 'generateDLEQ2Proof.scalar');
            const k = bytesToScalar(this.randomScalar());
            const R1 = B1.multiply(k);
            const R2 = B2.multiply(k);
            const challenge = lengthPrefixedHash([
                DLEQ2_DST,
                pointToBytes(B1),
                pointToBytes(P1),
                pointToBytes(B2),
                pointToBytes(P2),
                pointToBytes(R1),
                pointToBytes(R2),
                context,
            ]);
            const e = bytesToScalar(challenge);
            const z = Fr.create(Fr.sub(k, Fr.mul(e, s)));
            return {
                R1: pointToBytes(R1),
                R2: pointToBytes(R2),
                z: scalarToBytes(z),
            };
        },
        verifyDLEQ2Proof(proof, point1, point2, basePoint1, basePoint2, context) {
            assertUint8Array(point1, 'verifyDLEQ2Proof.point1', 48);
            assertUint8Array(point2, 'verifyDLEQ2Proof.point2', 48);
            assertUint8Array(basePoint1, 'verifyDLEQ2Proof.basePoint1', 48);
            assertUint8Array(basePoint2, 'verifyDLEQ2Proof.basePoint2', 48);
            assertUint8Array(context, 'verifyDLEQ2Proof.context');
            assertUint8Array(proof.R1, 'verifyDLEQ2Proof.proof.R1', 48);
            assertUint8Array(proof.R2, 'verifyDLEQ2Proof.proof.R2', 48);
            assertUint8Array(proof.z, 'verifyDLEQ2Proof.proof.z', 32);
            let P1;
            let P2;
            let B1;
            let B2;
            let R1;
            let R2;
            try {
                P1 = pointFromBytes(point1);
                P2 = pointFromBytes(point2);
                B1 = pointFromBytes(basePoint1);
                B2 = pointFromBytes(basePoint2);
                R1 = pointFromBytes(proof.R1);
                R2 = pointFromBytes(proof.R2);
            }
            catch {
                return false;
            }
            if (R1.equals(ProjectivePoint.ZERO) || R2.equals(ProjectivePoint.ZERO)) {
                return false;
            }
            const z = bytesToScalar(proof.z);
            const challenge = lengthPrefixedHash([
                DLEQ2_DST,
                pointToBytes(B1),
                pointToBytes(P1),
                pointToBytes(B2),
                pointToBytes(P2),
                pointToBytes(R1),
                pointToBytes(R2),
                context,
            ]);
            const e = bytesToScalar(challenge);
            const lhs1 = B1.multiply(z).add(P1.multiply(e));
            const lhs2 = B2.multiply(z).add(P2.multiply(e));
            return lhs1.equals(R1) && lhs2.equals(R2);
        },
        hmacSha256(key, msg) {
            assertUint8Array(key, 'hmacSha256.key');
            assertUint8Array(msg, 'hmacSha256.msg');
            return hmac(sha256, key, msg);
        },
        async verifyIssuerSignature(signature, message, publicKey, algorithm) {
            assertUint8Array(signature, 'verifyIssuerSignature.signature');
            assertUint8Array(message, 'verifyIssuerSignature.message');
            assertUint8Array(publicKey, 'verifyIssuerSignature.publicKey');
            switch (algorithm) {
                case 'ed25519':
                    try {
                        return ed25519.verify(signature, message, publicKey);
                    }
                    catch {
                        return false;
                    }
                case 'secp256k1':
                    try {
                        return secp256k1.verify(signature, sha256(message), publicKey);
                    }
                    catch {
                        return false;
                    }
                case 'rs256':
                    try {
                        const cryptoKey = await crypto.subtle.importKey('spki', publicKey.slice().buffer, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
                        return await crypto.subtle.verify('RSASSA-PKCS1-v1_5', cryptoKey, signature.slice().buffer, message.slice().buffer);
                    }
                    catch {
                        return false;
                    }
                case 'bls12381-g2':
                    return false;
                default:
                    return false;
            }
        },
        // BBS+ operations — backed by @mattrglobal/pairing-crypto (WASM, Rust).
        // Uses BLS12-381-SHA-256 ciphersuite per IETF BBS draft.
        // @traces §2.2, §2.4, F-D3
        async bbsGenerateKeyPair(seed) {
            const kp = await bbs.bls12381_sha256.generateKeyPair(seed ? { ikm: seed } : undefined);
            return { secretKey: kp.secretKey, publicKey: kp.publicKey };
        },
        async bbsSign({ secretKey, publicKey, messages, header }) {
            return bbs.bls12381_sha256.sign({
                secretKey,
                publicKey,
                header,
                messages,
            });
        },
        async bbsVerify({ publicKey, signature, messages, header }) {
            const result = await bbs.bls12381_sha256.verify({
                publicKey,
                signature,
                header,
                messages,
            });
            return result.verified;
        },
        async bbsDeriveProof({ publicKey, signature, messages, disclosedIndexes, header, presentationHeader, }) {
            const disclosedSet = new Set(disclosedIndexes);
            const messageArray = messages.map((value, i) => ({
                value,
                reveal: disclosedSet.has(i),
            }));
            return bbs.bls12381_sha256.deriveProof({
                publicKey,
                signature,
                header,
                presentationHeader,
                verifySignature: false,
                messages: messageArray,
            });
        },
        async bbsVerifyProof({ publicKey, proof, disclosedMessages, disclosedIndexes, header, presentationHeader, }) {
            const messages = {};
            for (let i = 0; i < disclosedIndexes.length; i++) {
                messages[disclosedIndexes[i]] = disclosedMessages[i];
            }
            const result = await bbs.bls12381_sha256.verifyProof({
                publicKey,
                proof,
                header,
                presentationHeader,
                messages,
            });
            return result.verified;
        },
        hash(data) {
            return sha256(data);
        },
        randomScalar() {
            for (let attempt = 0; attempt < 8; attempt++) {
                const bytes = new Uint8Array(32);
                crypto.getRandomValues(bytes);
                const scalar = Fr.create(bytesToScalar(bytes));
                if (scalar !== 0n) {
                    return scalarToBytes(scalar);
                }
            }
            throw new Error('randomScalar: RNG produced zero scalar 8 times in a row');
        },
    };
}
